import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ArrowRight, ChevronDown, QrCode, Smartphone, Zap, Code2, Link2, BarChart3, Store, Globe, Layers, Headphones, Users, FileText, ShieldCheck, Building2, RefreshCw, Receipt, BookOpen, Package } from "lucide-react";
import Reveal from "../components/Reveal";
import { A, DOCS_URL } from "../config";
import CurlPanel from "../components/CurlPanel";
import { FAQ, ECO } from "../data";
import { usePageMeta } from "../hooks";
import "./HomeEcosystem.css";

const float = { animate: { y: [0, -12, 0] }, transition: { duration: 6.5, repeat: Infinity, ease: "easeInOut" } };
const up = (d) => ({ initial: { opacity: 0, y: 26 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.75, delay: d, ease: [0.2, 0.7, 0.2, 1] } });
const ECO_ICONS = [Store, Layers, Building2, RefreshCw];
const TICK = ["UPI QR", "UPI Soundbox", "UPI Payment Gateway", "Merchant Onboarding", "Payment APIs", "Webhook Notifications", "Reconciliation"];
const ONB = [["Register Your Business", "Share your business details to begin.", Store], ["Submit KYC Documents", "Provide the documents required for verification.", FileText], ["Verification & Approval", "Review by Partner Banks, where applicable.", ShieldCheck], ["Activate Payment Solutions", "Integrate and go live once approved.", Zap]];
const UC = [["Retail Stores", Store], ["E-commerce Businesses", Globe], ["Digital Platforms", Layers], ["Service Providers", Headphones], ["Enterprises", BarChart3], ["Merchant Networks", Users]];
const WHY = [["Technology-driven Infrastructure", "Modern engineering behind every payment workflow.", Layers], ["Unified Payment Integration", "One connection across QR, Soundbox and gateway.", Zap], ["Merchant-focused Solutions", "Built around real counters and checkouts.", Store], ["Flexible API Architecture", "Adapts to your stack, not the other way around.", Code2], ["Transaction Visibility", "Clear status and reporting for every payment.", BarChart3], ["Dedicated Business Support", "Guidance from onboarding to integration.", Headphones]];
const PAY = [["UPI QR", "/upi-qr"], ["UPI Soundbox", "/upi-soundbox"], ["UPI Payment Gateway", "/upi-payment-gateway"]];
const BIZ = [["Smart Invoicing & GST Billing", "Invoices and GST billing records linked with payment tracking.", Receipt, "/products/invoicing-gst-billing"], ["Business Accounting", "Ledgers, receivables and cash-flow visibility for everyday operations.", BookOpen, "/products/business-accounting"], ["Retail POS & Inventory", "Retail billing, product catalogue and stock records for stores.", Package, "/products/pos-inventory"]];
const GWF = [["UPI Intent", Zap], ["QR Payments", QrCode], ["Pay via UPI App", Smartphone], ["Payment APIs", Code2], ["Webhook Notifications", Link2], ["Transaction Reconciliation", BarChart3]];

const DEV = [
  ["REST APIs", "Integrate secure UPI payment collection APIs using standard HTTPS requests and JSON responses."],
  ["Secure Integrations", "Connect using Basic Authentication with authorized API credentials and secure server-side requests."],
  ["Webhook Notifications", "Receive payment transaction updates through configured webhook callbacks."],
  ["Payment Status", "Track UPI payment transactions using the available transaction status APIs."],
  ["Developer Documentation", "Access API endpoints, request parameters, authentication details, and integration guides."],
  ["Integration Guides", "Follow step-by-step guidance in the developer documentation to connect your application."],
];

function DevList() {
  const [o, setO] = useState(0);
  return (
    <ul className="rows dk dax">
      {DEV.map(([t, d], i) => (
        <li key={t} className={o === i ? "open" : ""}>
          <button type="button" aria-expanded={o === i} onClick={() => setO(o === i ? -1 : i)}><span>{t}</span><ChevronDown size={18} /></button>
          {o === i && <p>{d}</p>}
        </li>
      ))}
    </ul>
  );
}

function Chapter({ id, n, cls, label, title, lead, items, cta, to, img, w, h, alt, note, rev }) {
  return (
    <section id={id} className={"ch " + cls}>
      <div className="num" aria-hidden="true">{n}</div>
      <div className={"w cg" + (rev ? " rev" : "")}>
        <Reveal className="ct" dir={rev ? "right" : "left"}>
          <div className="lab"><i />{n} — {label}</div>
          <h2>{title}</h2>
          <p className="lead">{lead}</p>
          <ul className="rows">{items.map((x) => <li key={x}>{x}</li>)}</ul>
          {note && <p className="sm">{note}</p>}
          <Link className="go" to={to}><span>{cta}</span><b><ArrowRight size={20} /></b></Link>
        </Reveal>
        <Reveal className="stage" dir="zoom">
          <motion.img className="pimg" src={A + img} width={w} height={h} loading="lazy" alt={alt} {...float} />
        </Reveal>
      </div>
    </section>
  );
}

function Faq() {
  const [o, setO] = useState(0);
  return (
    <div>
      {FAQ.map(([q, a], i) => (
        <div key={q} className={"fqi" + (o === i ? " open" : "")}>
          <button type="button" aria-expanded={o === i} onClick={() => setO(o === i ? -1 : i)}>
            <span>{q}</span><ChevronDown size={20} className="chv" />
          </button>
          <AnimatePresence initial={false}>
            {o === i && (
              <motion.div key="c" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} style={{ overflow: "hidden" }}>
                <p>{a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  usePageMeta("Formax Connect — One Connection. Endless Payment Possibilities.", "Formax Connect: UPI QR, UPI Soundbox and UPI payment gateway technology for merchants, platforms and enterprises.");
  const [eco, setEco] = useState(-1);
  const tl = useRef(null);
  const tlIn = useInView(tl, { once: true, margin: "-120px" });

  return (
    <>
      <section className="h3">
        <div className="mesh" />
        <div className="w h3in">
          <div className="h3t">
            <motion.div className="kick" {...up(0)}>Next-Generation UPI Payment Infrastructure</motion.div>
            <motion.h1 {...up(0.08)}>One Connection. <em>Endless Payment Possibilities.</em></motion.h1>
            <motion.p className="lead" {...up(0.16)}>Power your business with seamless UPI collections, smart QR payments, soundbox solutions, and scalable payment gateway technology.</motion.p>
            <motion.div className="row" {...up(0.24)}>
              <a className="btn hp arr" href="#products">Explore Our Products <ArrowRight size={16} /></a>
              <Link className="btn hg" to="/contact-us">Talk to Sales</Link>
            </motion.div>
            <motion.nav className="pl3" aria-label="Products" {...up(0.32)}>
              {[["qr", "UPI QR"], ["sb", "UPI Soundbox"], ["gw", "UPI Payment Gateway"]].map(([h, t], i) => (
                <a key={h} href={"#" + h}><b>0{i + 1}</b>{t}</a>
              ))}
            </motion.nav>
          </div>
          <div className="h3v">
            <div className="hglow" />
            <motion.img className="himg" src={A + "hero.webp"} width="1500" height="938" alt="Formax Connect merchant payment dashboard with UPI QR stand and mobile app" initial={{ opacity: 0, scale: 0.94, x: 40 }} animate={{ opacity: 1, scale: 1, x: 0, y: [0, -12, 0] }} transition={{ opacity: { duration: 0.9 }, scale: { duration: 0.9 }, x: { duration: 0.9 }, y: { duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.9 } }} />
          </div>
        </div>
      </section>

      <div className="tick" aria-hidden="true">
        <div>{[0, 1].map((k) => TICK.map((x) => <span key={k + x}>{x}</span>))}</div>
      </div>
      <div id="products" />

      <Chapter id="qr" n="01" cls="c1" label="UPI QR" title="Accept UPI Payments with Smart QR Solutions" lead="Make payment collection simpler for merchants with QR-based UPI payment experiences." items={["Static UPI QR", "Dynamic UPI QR", "Merchant payment collection", "Transaction notifications", "Reporting and reconciliation"]} cta="Explore UPI QR" to="/upi-qr" img="qr.webp" w={760} h={1140} alt="Formax Connect UPI QR stand" />
      <Chapter id="sb" n="02" cls="c2" label="UPI Soundbox" title="Hear Every Payment. Stay Informed." lead="Explore audio-enabled payment confirmation solutions designed for busy retail environments." items={["Voice payment confirmation", "Merchant-friendly device experience", "QR payment ecosystem", "Transaction visibility", "Device management concepts"]} cta="Explore UPI Soundbox" to="/upi-soundbox" img="sb.webp" w={820} h={820} alt="Formax Connect UPI Soundbox" note="Product visuals are representative. Specifications and availability are confirmed during enquiry." rev />

      <section id="gw" className="gwx">
        <div className="w">
          <div className="gwh">
            <Reveal dir="left">
              <div className="lab"><i />03 — UPI Payment Gateway</div>
              <h2>Seamless Online Payments for Growing Businesses</h2>
              <p className="lead">Integrate UPI payment experiences into your website or application through flexible payment technology.</p>
              <Link className="go" to="/upi-payment-gateway"><span>Explore Payment Gateway</span><b><ArrowRight size={20} /></b></Link>
            </Reveal>
            <Reveal className="gf" dir="right">
              {GWF.map(([t, Icon]) => <div key={t}><Icon size={22} /><b>{t}</b></div>)}
            </Reveal>
          </div>
          <Reveal className="gwi" dir="zoom">
            <img src={A + "gw.webp"} width="1600" height="900" loading="lazy" alt="Formax Connect payment gateway: browser checkout, UPI app confirmation and API integration" />
          </Reveal>
          <p className="sm" style={{ textAlign: "center" }}>Capabilities depend on Partner Bank requirements and are subject to approval.</p>
        </div>
      </section>

      <section className="bxe" aria-labelledby="bxe-h">
        <div className="w">
          <Reveal className="bxh"><div className="eb">The Formax Connect Ecosystem</div><h2 id="bxe-h">Beyond Payments. Built for Business.</h2><p>Formax Connect is expanding beyond digital payment acceptance toward connected invoicing, GST billing, accounting, and retail business management solutions.</p></Reveal>
          <div className="bxg">
            <div className="bxl bxl1" aria-hidden="true"><span>Payments</span></div>
            <Reveal className="bxw bxw1">
              <div className="bxc bxp">
                <span className="bxi"><Zap size={24} /></span>
                <h3><a href="#products">Digital Payments</a></h3>
                <p>UPI QR, UPI Soundbox and UPI Payment Gateway technology for in-store and online collections.</p>
                <nav className="bxk" aria-label="Payment products">{PAY.map(([t, to]) => <Link key={to} to={to}>{t}</Link>)}</nav>
              </div>
            </Reveal>
            <div className="bxl bxl2" aria-hidden="true"><span>Business</span></div>
            {BIZ.map(([t, d, Icon, to], i) => (
              <Reveal className="bxw" key={t} delay={(i + 1) * 0.08}>
                <Link to={to} className="bxc bxa">
                  <span className="bxi"><Icon size={24} /></span>
                  <h3>{t}</h3>
                  <p>{d}</p>
                  <span className="bxm">Explore <ArrowRight size={16} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="infra">
        <div className="w">
          <Reveal><div className="eb">Unified Payment Infrastructure</div><h2>One Platform, Multiple Capabilities</h2></Reveal>
          <div className="eco">
            {ECO.map(([a], i) => (
              <button key={a} type="button" className={eco === i ? "on" : ""} onMouseEnter={() => setEco(i)} onClick={() => setEco(i)} onFocus={() => setEco(i)}>
                <small>{React.createElement(ECO_ICONS[i], { size: 24 })}</small>{a}
              </button>
            ))}
          </div>
          <p id="ecot">{eco >= 0 ? ECO[eco][1] : "Hover or tap a step to learn more."}</p>
          <p className="sm">Formax Connect does not independently operate banking or payment settlement rails.</p>
        </div>
      </section>

      <section className="onb">
        <div className="w og">
          <Reveal className="os" dir="left">
            <div className="eb">Merchant Onboarding</div><h2>Get Connected in Four Steps</h2>
            <p className="lead">A guided journey from registration to activation.</p>
            <div className="note">Onboarding and activation are subject to eligibility, verification and Partner Bank approval.</div>
          </Reveal>
          <div ref={tl} className={"vt" + (tlIn ? " in" : "")}>
            {ONB.map(([t, d, Icon], i) => (
              <div className="vs" key={t} style={{ "--d": i * 0.3 + "s" }}>
                <b><Icon size={24} /></b>
                <div><h3>{t}</h3><p>{d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="dv">
        <div className="w dg">
          <Reveal dir="left">
            <div className="eb">Developers</div>
            <h2>Built for Developers. Designed for Scale.</h2>
            <DevList />
            <a className="go lt" href={DOCS_URL} target="_blank" rel="noopener noreferrer"><span>Explore Developer APIs</span><b><ArrowRight size={20} /></b></a>
          </Reveal>
          <Reveal dir="right">
            <CurlPanel />
            <div className="arc"><span>Your App</span><ArrowRight size={16} /><span>Formax Connect</span><ArrowRight size={16} /><span>Partner Banks</span><ArrowRight size={16} /><span>Reports</span></div>
          </Reveal>
        </div>
      </section>

      <section className="uc3">
        <div className="w">
          <Reveal><div className="eb">Business Use Cases</div><h2>Built for Every Kind of Business</h2></Reveal>
          <div className="ul">
            {UC.map(([t, Icon], i) => (
              <Reveal className="ur" key={t} delay={i * 0.05}>
                <span className="uic"><Icon size={24} /></span><b>{t}</b><ArrowRight size={18} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="why">
        <div className="w">
          <Reveal><div className="eb">Why Choose Formax Connect</div><h2>Enterprise-Grade. Merchant-First.</h2></Reveal>
          <div className="wg">
            {WHY.map(([t, d, Icon], i) => (
              <Reveal className="wi" key={t} delay={i * 0.07}>
                <span className="wic"><Icon size={26} /></span><h3>{t}</h3><p>{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="fq">
        <div className="w fg2">
          <Reveal dir="left"><div className="eb">FAQ</div><h2>Frequently Asked Questions</h2></Reveal>
          <Faq />
        </div>
      </section>
    </>
  );
}
